'use client';

import { useState } from 'react';
import { useTheme } from '@/components/ThemeProvider';

export interface OutageFilterState {
  vendor: string;
  severity: string;
  status: string;
  startDate: string;
  endDate: string;
  impact_scope: string;
}

interface OutageFiltersProps {
  filters: OutageFilterState;
  onFilterChange: (filters: OutageFilterState) => void;
  vendors: string[];
}

export function OutageFilters({ filters, onFilterChange, vendors }: OutageFiltersProps) {
  const { theme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(true);

  const handleChange = (key: keyof OutageFilterState, value: string) => {
    onFilterChange({
      ...filters,
      [key]: value,
    });
  };

  const handleReset = () => {
    onFilterChange({
      vendor: '',
      severity: '',
      status: '',
      startDate: '',
      endDate: '',
      impact_scope: '',
    });
  };

  const applyQuickFilter = (filterType: 'critical' | '2025' | 'global' | 'recent') => {
    const now = new Date();
    switch (filterType) {
      case 'critical':
        onFilterChange({ ...filters, severity: 'critical' });
        break;
      case '2025':
        onFilterChange({ ...filters, startDate: '2025-01-01', endDate: '2025-12-31' });
        break;
      case 'global':
        onFilterChange({ ...filters, impact_scope: 'global' });
        break;
      case 'recent':
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        onFilterChange({
          ...filters,
          startDate: thirtyDaysAgo.toISOString().split('T')[0],
          endDate: now.toISOString().split('T')[0]
        });
        break;
    }
  };

  const activeFiltersCount = Object.values(filters).filter(v => v !== '').length;

  return (
    <div
      className="rounded-lg p-6 mb-8 shadow-sm"
      style={{
        backgroundColor: theme === 'dark' ? 'rgba(10, 30, 46, 0.5)' : 'white',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: theme === 'dark' ? 'rgba(232, 213, 183, 0.1)' : '#d1d5db'
      }}
    >
      {/* Filter Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <h3 style={{ color: theme === 'dark' ? '#E8D5B7' : '#111827' }} className="text-lg font-semibold">Filters</h3>
          {activeFiltersCount > 0 && (
            <span className="px-2 py-1 bg-accent-muted text-accent text-xs rounded">
              {activeFiltersCount} active
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {activeFiltersCount > 0 && (
            <button
              onClick={handleReset}
              style={{ color: theme === 'dark' ? 'rgba(232, 213, 183, 0.6)' : '#4b5563' }}
              className="text-sm hover:text-accent transition-colors"
            >
              Reset all
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-accent hover:text-accent transition-colors text-sm"
          >
            {isExpanded ? 'Hide filters ▲' : 'Show filters ▼'}
          </button>
        </div>
      </div>

      {/* Quick Filters */}
      <div className="space-y-4 mb-6">
        <div>
          <h4 style={{ color: theme === 'dark' ? 'rgba(232, 213, 183, 0.7)' : '#1f2937' }} className="text-sm font-medium mb-2">Quick Filters</h4>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => applyQuickFilter('critical')}
              className="px-3 py-1.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/30 text-red-700 dark:text-red-300 rounded-lg text-sm hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
            >
              🔴 Critical Only
            </button>
            <button
              onClick={() => applyQuickFilter('2025')}
              className="px-3 py-1.5 bg-accent-muted border border-border text-accent rounded-lg text-sm hover:bg-accent-muted transition-colors"
            >
              📅 2025
            </button>
            <button
              onClick={() => applyQuickFilter('global')}
              className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/30 text-blue-700 dark:text-blue-300 rounded-lg text-sm hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
            >
              🌍 Global Impact
            </button>
            <button
              onClick={() => applyQuickFilter('recent')}
              className="px-3 py-1.5 bg-gray-100 dark:bg-cream/5 border border-gray-300 dark:border-border text-gray-700 dark:text-foreground rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-cream/10 transition-colors"
            >
              ⏱️ Last 30 Days
            </button>
          </div>
        </div>

        {/* Major Vendor Quick Filters */}
        {vendors.length > 0 && (
          <div>
            <h4 style={{ color: theme === 'dark' ? 'rgba(232, 213, 183, 0.7)' : '#1f2937' }} className="text-sm font-medium mb-2">Major Vendors</h4>
            <div className="flex flex-wrap gap-2">
              {['AWS', 'Google Cloud', 'Microsoft Azure', 'Cloudflare', 'OpenAI', 'Vercel'].map((vendor) => {
                if (!vendors.includes(vendor)) return null;
                return (
                  <button
                    key={vendor}
                    onClick={() => handleChange('vendor', vendor)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      filters.vendor === vendor
                        ? 'bg-accent text-white dark:text-accent-foreground font-semibold'
                        : 'bg-gray-100 dark:bg-cream/5 border border-gray-200 dark:border-border text-gray-700 dark:text-foreground hover:bg-gray-200 dark:hover:bg-cream/10'
                    }`}
                  >
                    {vendor}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Filter Fields */}
      {isExpanded && (
        <>
          <div className="border-t border-gray-200 dark:border-border-muted mb-6"></div>
          <div className="space-y-6">
            <div>
              <h4 style={{ color: theme === 'dark' ? 'rgba(232, 213, 183, 0.7)' : '#1f2937' }} className="text-sm font-medium mb-4">Advanced Filters</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Vendor Filter */}
                <div>
                  <label style={{ color: theme === 'dark' ? 'rgba(232, 213, 183, 0.7)' : '#1f2937' }} className="block text-sm mb-2">Vendor</label>
                  <select
                    value={filters.vendor}
                    onChange={(e) => handleChange('vendor', e.target.value)}
                    className="w-full bg-white dark:bg-background border border-gray-300 dark:border-border text-gray-900 dark:text-foreground rounded-lg px-3 py-2 focus:outline-none focus:border-accent focus:ring-1 focus:ring-ring transition-colors"
                  >
                    <option value="">All vendors</option>
                    {vendors.map((vendor) => (
                      <option key={vendor} value={vendor}>
                        {vendor}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Severity Filter */}
                <div>
                  <label style={{ color: theme === 'dark' ? 'rgba(232, 213, 183, 0.7)' : '#1f2937' }} className="block text-sm mb-2">Severity</label>
                  <select
                    value={filters.severity}
                    onChange={(e) => handleChange('severity', e.target.value)}
                    className="w-full bg-white dark:bg-background border border-gray-300 dark:border-border text-gray-900 dark:text-foreground rounded-lg px-3 py-2 focus:outline-none focus:border-accent focus:ring-1 focus:ring-ring transition-colors"
                  >
                    <option value="">All severities</option>
                    <option value="critical">Critical</option>
                    <option value="major">Major</option>
                    <option value="moderate">Moderate</option>
                  </select>
                </div>

                {/* Status Filter */}
                <div>
                  <label style={{ color: theme === 'dark' ? 'rgba(232, 213, 183, 0.7)' : '#1f2937' }} className="block text-sm mb-2">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    className="w-full bg-white dark:bg-background border border-gray-300 dark:border-border text-gray-900 dark:text-foreground rounded-lg px-3 py-2 focus:outline-none focus:border-accent focus:ring-1 focus:ring-ring transition-colors"
                  >
                    <option value="">All statuses</option>
                    <option value="ongoing">Ongoing</option>
                    <option value="resolved">Resolved</option>
                    <option value="investigating">Investigating</option>
                  </select>
                </div>

                {/* Impact Scope Filter */}
                <div>
                  <label style={{ color: theme === 'dark' ? 'rgba(232, 213, 183, 0.7)' : '#1f2937' }} className="block text-sm mb-2">Impact Scope</label>
                  <select
                    value={filters.impact_scope}
                    onChange={(e) => handleChange('impact_scope', e.target.value)}
                    className="w-full bg-white dark:bg-background border border-gray-300 dark:border-border text-gray-900 dark:text-foreground rounded-lg px-3 py-2 focus:outline-none focus:border-accent focus:ring-1 focus:ring-ring transition-colors"
                  >
                    <option value="">All scopes</option>
                    <option value="global">Global</option>
                    <option value="multi-region">Multi-region</option>
                    <option value="us-east-1">US-EAST-1</option>
                    <option value="na14-instance">Specific Instance</option>
                  </select>
                </div>

                {/* Start Date Filter */}
                <div>
                  <label style={{ color: theme === 'dark' ? 'rgba(232, 213, 183, 0.7)' : '#1f2937' }} className="block text-sm mb-2">Start Date (From)</label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleChange('startDate', e.target.value)}
                    className="w-full bg-white dark:bg-background border border-gray-300 dark:border-border text-gray-900 dark:text-foreground rounded-lg px-3 py-2 focus:outline-none focus:border-accent focus:ring-1 focus:ring-ring transition-colors"
                  />
                </div>

                {/* End Date Filter */}
                <div>
                  <label style={{ color: theme === 'dark' ? 'rgba(232, 213, 183, 0.7)' : '#1f2937' }} className="block text-sm mb-2">Start Date (To)</label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleChange('endDate', e.target.value)}
                    className="w-full bg-white dark:bg-background border border-gray-300 dark:border-border text-gray-900 dark:text-foreground rounded-lg px-3 py-2 focus:outline-none focus:border-accent focus:ring-1 focus:ring-ring transition-colors"
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
